import React, { useEffect, useRef, useState } from "react";
import { getConfig, updateConfig } from "../../services/api";
import { getUser } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import globalStyles, { COLORS } from "../../styles/globalStyles";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const STAGE_PER = 5;
const STATUS_PER = 5;
const BUSINESS_PER = 5;
const LANGUAGE_PER = 5;
const NOTIF_CHANNELS = ["sms", "push", "whatsapp"];

const DEFAULT_CONFIG = {
  language: { displayName: "English", key: "en", value: "en" },
  notificationSettings: { sms: true, push: true, whatsapp: false },
  leadStages: [
    "prospecting",
    "contacted",
    "demo_scheduled",
    "demo_done",
    "negotiation",
    "closed",
  ],
  statuses: {
    prospecting: ["pending", "in_progress"],
    contacted: ["pending", "in_progress", "no_response"],
    demo_scheduled: ["pending", "confirmed", "cancelled"],
    demo_done: ["pending", "completed"],
    negotiation: ["pending", "in_progress", "on_hold"],
    closed: ["closed_won", "closed_lost"],
  },
  incentives: { per_lead: 50, per_demo: 150, per_closure: 500 },
  businessType: "digital cable,broadband,both",
  supportedLanguages: [
    { key: "en", value: "en", displayName: "English" },
    { key: "hi", value: "hi", displayName: "Hindi" },
  ],
};

const s = {
  h1: { fontSize: 22, fontWeight: 700, color: "#1e293b", marginBottom: 24 },
  card: {
    background: "#fff",
    padding: "14px 28px",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: 24,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
    marginBottom: 24,
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  h2: { fontSize: 16, fontWeight: 600, color: "#1e293b", margin: 0 },
  editBtn: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: COLORS.primary,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  listWrap: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    overflow: "hidden",
    marginTop: 12,
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 14,
    color: "#374151",
    background: "#fff",
    gap: 10,
  },
  itemName: { textTransform: "capitalize", fontWeight: 500 },
  itemMeta: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 600,
    textTransform: "capitalize",
  },
  empty: { padding: 14, fontSize: 13, color: "#64748b", background: "#f8fafc" },
  btn: {
    padding: "10px 20px",
    background: COLORS.primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  toggleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  toggleBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  success: {
    color: "#065f46",
    background: "#d1fae5",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  error: {
    color: "#ef4444",
    background: "#fee2e2",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 14,
  },
  tagInputWrap: {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "6px 10px",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    background: "#fff",
    marginBottom: 12,
    minHeight: 42,
    cursor: "text",
  },
  tagInputWrapError: { borderColor: "#ef4444" },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#EEEDFA",
    color: COLORS.primary,
    borderRadius: 6,
    padding: "3px 8px",
    fontSize: 13,
    fontWeight: 500,
    textTransform: "capitalize",
  },
  tagRemoveBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    color: COLORS.primary,
    display: "flex",
    alignItems: "center",
    lineHeight: 1,
  },
  tagInlineInput: {
    border: "none",
    outline: "none",
    fontSize: 13,
    minWidth: 120,
    background: "transparent",
    color: "#1e293b",
    padding: "2px 0",
    flex: 1,
  },
  addInputRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginTop: 8,
    flexWrap: "wrap",
  },
  addInput: {
    flex: 1,
    minWidth: 120,
    padding: "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  addBtn: {
    padding: "8px 14px",
    background: COLORS.primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
  },
  inlineRemoveBtn: {
    border: "none",
    background: "#fee2e2",
    color: "#ef4444",
    width: 24,
    height: 24,
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  incentiveValueRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    gap: 12,
  },
  incentiveName: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
    textTransform: "capitalize",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  incentiveDotYellow: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#f59e0b",
  },
  incentiveDotBlue: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#3b82f6",
  },
  incentiveDotGreen: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
  },
  incentiveAmount: { fontSize: 15, fontWeight: 700, color: COLORS.primary },
  notifIcon: { display: "flex", alignItems: "center", gap: 8 },
  notifLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1e293b",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  notifStatus: { fontSize: 11, fontWeight: 500, marginTop: 1 },
  spinner: {
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "cfgspin 0.7s linear infinite",
  },
};

const NOTIF_ICONS = {
  sms: <SmsOutlinedIcon style={{ fontSize: 18, color: COLORS.primary }} />,
  push: (
    <PhoneAndroidOutlinedIcon style={{ fontSize: 18, color: COLORS.primary }} />
  ),
  whatsapp: <WhatsAppIcon style={{ fontSize: 18, color: "#25d366" }} />,
};

const INCENTIVE_DOTS = {
  per_lead: <span style={s.incentiveDotYellow} />,
  per_demo: <span style={s.incentiveDotBlue} />,
  per_closure: <span style={s.incentiveDotGreen} />,
};

function formatLabel(value) {
  if (!value) return "—";
  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getBusinessTypes(config) {
  if (Array.isArray(config?.businessTypes) && config.businessTypes.length) {
    return config.businessTypes
      .map((item) => String(item).trim())
      .filter(Boolean);
  }
  if (config?.businessType) {
    return config.businessType
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeLanguage(language, supportedLanguages = []) {
  const fallbackLanguages =
    supportedLanguages.length > 0
      ? supportedLanguages
      : DEFAULT_CONFIG.supportedLanguages;

  if (language && typeof language === "object") {
    const key = String(language.key || language.value || "")
      .trim()
      .toLowerCase();
    const matched = fallbackLanguages.find((lang) => lang.key === key);

    if (!key && !matched?.key) {
      return fallbackLanguages[0] || DEFAULT_CONFIG.language;
    }

    return {
      key: key || matched?.key || DEFAULT_CONFIG.language.key,
      value: key || matched?.value || DEFAULT_CONFIG.language.value,
      displayName:
        language.displayName ||
        matched?.displayName ||
        DEFAULT_CONFIG.language.displayName,
    };
  }

  if (typeof language === "string") {
    const key = language.trim().toLowerCase();
    const matched = fallbackLanguages.find(
      (lang) => lang.key === key || lang.value === key,
    );

    return (
      matched || {
        key: key || DEFAULT_CONFIG.language.key,
        value: key || DEFAULT_CONFIG.language.value,
        displayName: formatLabel(key || DEFAULT_CONFIG.language.displayName),
      }
    );
  }

  return fallbackLanguages[0] || DEFAULT_CONFIG.language;
}

function normalizeConfig(data = {}) {
  const merged = { ...DEFAULT_CONFIG, ...data };
  const supportedLanguages =
    Array.isArray(merged.supportedLanguages) && merged.supportedLanguages.length
      ? merged.supportedLanguages.map((lang) => ({
          key: String(lang.key || lang.value || "")
            .trim()
            .toLowerCase(),
          value: String(lang.value || lang.key || "")
            .trim()
            .toLowerCase(),
          displayName: lang.displayName || formatLabel(lang.key || lang.value),
        }))
      : DEFAULT_CONFIG.supportedLanguages;

  return {
    ...merged,
    supportedLanguages,
    language: normalizeLanguage(merged.language, supportedLanguages),
  };
}

function TagArrayInput({
  value = [],
  onChange,
  placeholder,
  hasError,
  normalize,
}) {
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);

  const addTag = (raw) => {
    const prepared = normalize ? normalize(raw) : raw.trim();
    if (!prepared || value.includes(prepared)) return;
    onChange([...value, prepared]);
    setInputVal("");
  };

  const removeTag = (tag) => onChange(value.filter((item) => item !== tag));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputVal);
    }
    if (e.key === "Backspace" && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div
      style={{ ...s.tagInputWrap, ...(hasError ? s.tagInputWrapError : {}) }}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag) => (
        <span key={tag} style={s.tag}>
          {tag.replaceAll("_", " ")}
          <button
            type="button"
            style={s.tagRemoveBtn}
            onClick={(e) => {
              e.stopPropagation();
              removeTag(tag);
            }}
          >
            <CloseIcon style={{ fontSize: 13 }} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        style={s.tagInlineInput}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => inputVal.trim() && addTag(inputVal)}
        placeholder={value.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div style={globalStyles.searchWrap}>
      <SearchIcon style={globalStyles.searchIcon} />
      <input
        style={globalStyles.searchInput}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}

function Pagination({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  return (
    <div style={globalStyles.paginateRow}>
      <button
        type="button"
        style={{ ...globalStyles.paginateBtn, opacity: page === 1 ? 0.5 : 1 }}
        disabled={page === 1}
        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
      >
        ‹
      </button>
      <span style={globalStyles.paginatePageInfo}>
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        style={{
          ...globalStyles.paginateBtn,
          opacity: page === totalPages ? 0.5 : 1,
        }}
        disabled={page === totalPages}
        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
      >
        ›
      </button>
    </div>
  );
}

export default function Config() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [fieldErr, setFieldErr] = useState({});
  const [statusesRaw, setStatusesRaw] = useState("");
  const [stageSearch, setStageSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");
  const [businessSearch, setBusinessSearch] = useState("");
  const [languageSearch, setLanguageSearch] = useState("");
  const [showStageInput, setShowStageInput] = useState(false);
  const [showStatusInput, setShowStatusInput] = useState(false);
  const [showIncentiveInput, setShowIncentiveInput] = useState(false);
  const [showBizInput, setShowBizInput] = useState(false);
  const [showLangInput, setShowLangInput] = useState(false);
  const [stagePage, setStagePage] = useState(1);
  const [statusPage, setStatusPage] = useState(1);
  const [businessPage, setBusinessPage] = useState(1);
  const [languagePage, setLanguagePage] = useState(1);
  const [newLangKey, setNewLangKey] = useState("");
  const [newLangName, setNewLangName] = useState("");

  const loggedInUser = getUser();
  const isAdmin = loggedInUser?.role === "admin";

  useEffect(() => {
    if (!isAdmin) {
      navigate("/unauthorized");
      return;
    }

    getConfig()
      .then((response) => {
        const data = response?.data?.data || DEFAULT_CONFIG;
        const normalizedConfig = normalizeConfig(data);

        setConfig(normalizedConfig);
        setStatusesRaw(
          JSON.stringify(
            normalizedConfig.statuses || DEFAULT_CONFIG.statuses,
            null,
            2,
          ),
        );
      })
      .catch(() => {
        const normalizedConfig = normalizeConfig(DEFAULT_CONFIG);
        setConfig(normalizedConfig);
        setStatusesRaw(JSON.stringify(normalizedConfig.statuses, null, 2));
        setMsg({ type: "error", text: "Failed to load configuration." });
      })
      .finally(() => setLoading(false));
  }, [isAdmin, navigate]);

  const getFieldError = (key) => {
    const err = fieldErr[key];
    return Array.isArray(err) ? err[0] : err || "";
  };

  const clearFieldError = (key) => {
    setFieldErr((prev) => ({ ...prev, [key]: "" }));
  };

  const handleToggle = (key) => {
    setConfig((prev) => ({
      ...prev,
      notificationSettings: {
        ...prev.notificationSettings,
        [key]: !prev.notificationSettings?.[key],
      },
    }));
  };

  const handleIncentiveChange = (key, val) => {
    setConfig((prev) => ({
      ...prev,
      incentives: { ...prev.incentives, [key]: val },
    }));
  };

  const handleStagesChange = (stages) => {
    setConfig((prev) => ({ ...prev, leadStages: stages }));
    clearFieldError("leadStages");
    setStagePage(1);
  };

  const handleBusinessTypesChange = (businessTypes) => {
    setConfig((prev) => ({
      ...prev,
      businessTypes,
      businessType: businessTypes.join(","),
    }));
    clearFieldError("businessType");
    clearFieldError("businessTypes");
    setBusinessPage(1);
  };

  const handleAddLang = () => {
    const key = newLangKey.trim().toLowerCase().replace(/\s+/g, "_");
    const displayName = newLangName.trim();

    if (!key || !displayName) return;

    const existing = config.supportedLanguages || [];

    if (existing.some((lang) => lang.key === key)) return;

    const newEntry = { key, value: key, displayName };

    setConfig((prev) => ({
      ...prev,
      supportedLanguages: [...existing, newEntry],
    }));

    clearFieldError("supportedLanguages");
    clearFieldError("language");
    setNewLangKey("");
    setNewLangName("");
    setLanguagePage(1);
  };

  const handleRemoveLang = (key) => {
    setConfig((prev) => ({
      ...prev,
      supportedLanguages: (prev.supportedLanguages || []).filter(
        (lang) => lang.key !== key,
      ),
    }));

    clearFieldError("supportedLanguages");
    clearFieldError("language");
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg({ type: "", text: "" });
    setFieldErr({});

    try {
      let updatedStatuses = {};
      try {
        updatedStatuses = JSON.parse(statusesRaw || "{}");
      } catch {
        setMsg({ type: "error", text: "Statuses JSON format is invalid." });
        setSaving(false);
        return;
      }

      const businessTypes = getBusinessTypes(config);
      const supportedLanguages =
        Array.isArray(config.supportedLanguages) &&
        config.supportedLanguages.length
          ? config.supportedLanguages
          : DEFAULT_CONFIG.supportedLanguages;

      const payload = {
        notificationSettings:
          config.notificationSettings || DEFAULT_CONFIG.notificationSettings,
        businessTypes,
        businessType: businessTypes.join(","),
        leadStages: config.leadStages || [],
        statuses: updatedStatuses,
        incentives: {
          per_lead: Number(config.incentives?.per_lead || 0),
          per_demo: Number(config.incentives?.per_demo || 0),
          per_closure: Number(config.incentives?.per_closure || 0),
        },
        supportedLanguages,
      };

      await updateConfig(payload);
      setConfig((prev) => ({ ...prev, ...payload }));
      setStatusesRaw(JSON.stringify(updatedStatuses, null, 2));
      setShowStageInput(false);
      setShowStatusInput(false);
      setShowIncentiveInput(false);
      setShowBizInput(false);
      setShowLangInput(false);
      setMsg({ type: "success", text: "Configuration saved successfully." });
    } catch (err) {
      setFieldErr(err.response?.data?.errors || {});
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Saving configuration failed.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading config...</div>;
  if (!config) return <div>Failed to load config.</div>;

  const leadStages = (config.leadStages || []).filter(Boolean);
  const filteredStages = leadStages.filter((stage) =>
    stage.toLowerCase().includes(stageSearch.toLowerCase()),
  );
  const totalStagePages = Math.ceil(filteredStages.length / STAGE_PER) || 1;
  const pagedStages = filteredStages.slice(
    (stagePage - 1) * STAGE_PER,
    stagePage * STAGE_PER,
  );

  const statusRows = Object.entries(config.statuses || {}).flatMap(
    ([stage, list]) => (list || []).map((status) => ({ stage, status })),
  );
  const filteredStatuses = statusRows.filter(({ stage, status }) => {
    const keyword = statusSearch.toLowerCase();
    return (
      stage.toLowerCase().includes(keyword) ||
      status.toLowerCase().includes(keyword)
    );
  });
  const totalStatusPages = Math.ceil(filteredStatuses.length / STATUS_PER) || 1;
  const pagedStatuses = filteredStatuses.slice(
    (statusPage - 1) * STATUS_PER,
    statusPage * STATUS_PER,
  );

  const businessTypes = getBusinessTypes(config);
  const filteredBusinessTypes = businessTypes.filter((type) =>
    type.toLowerCase().includes(businessSearch.toLowerCase()),
  );
  const totalBusinessPages =
    Math.ceil(filteredBusinessTypes.length / BUSINESS_PER) || 1;
  const pagedBusinessTypes = filteredBusinessTypes.slice(
    (businessPage - 1) * BUSINESS_PER,
    businessPage * BUSINESS_PER,
  );

  const supportedLangs = config.supportedLanguages || [];
  const currentLang = normalizeLanguage(config.language, supportedLangs);
  const filteredLanguages = supportedLangs.filter((lang) => {
    const keyword = languageSearch.toLowerCase();
    return (
      lang.displayName?.toLowerCase().includes(keyword) ||
      lang.key?.toLowerCase().includes(keyword) ||
      lang.value?.toLowerCase().includes(keyword)
    );
  });
  const totalLanguagePages =
    Math.ceil(filteredLanguages.length / LANGUAGE_PER) || 1;
  const pagedLanguages = filteredLanguages.slice(
    (languagePage - 1) * LANGUAGE_PER,
    languagePage * LANGUAGE_PER,
  );

  return (
    <div style={globalStyles.contentWrapper}>
      <style>{`@keyframes cfgspin { to { transform: rotate(360deg); } }`}</style>

      <h1 style={s.h1}>System Configuration</h1>

      {msg.text && (
        <div style={msg.type === "success" ? s.success : s.error}>
          {msg.type === "success" && <CheckIcon style={{ fontSize: 15 }} />}
          {msg.text}
        </div>
      )}

      <div style={s.row}>
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Lead Stages</h2>
            {/* {isAdmin && (
              <button
                type="button"
                style={s.editBtn}
                onClick={() => setShowStageInput((prev) => !prev)}
              >
                {showStageInput ? (
                  <RemoveIcon style={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                )}
              </button>
            )} */}
          </div>

          {showStageInput && (
            <div style={{ marginTop: 4, marginBottom: 4 }}>
              <label style={globalStyles.label}>Add stages</label>
              <TagArrayInput
                value={config.leadStages || []}
                onChange={handleStagesChange}
                placeholder="e.g. prospecting"
                hasError={!!getFieldError("leadStages")}
                normalize={(raw) =>
                  raw.trim().toLowerCase().replace(/\s+/g, "_")
                }
              />
              {getFieldError("leadStages") && (
                <div style={globalStyles.errorText}>
                  {getFieldError("leadStages")}
                </div>
              )}
            </div>
          )}

          <SearchBox
            value={stageSearch}
            onChange={(e) => {
              setStageSearch(e.target.value);
              setStagePage(1);
            }}
            placeholder="Search Lead Stage"
          />

          <div style={s.listWrap}>
            {pagedStages.length > 0 ? (
              pagedStages.map((stage) => (
                <div key={stage} style={s.listItem}>
                  <span style={s.itemName}>{stage.replaceAll("_", " ")}</span>
                  <span style={s.itemMeta}>
                    #{leadStages.indexOf(stage) + 1}
                  </span>
                </div>
              ))
            ) : (
              <div style={s.empty}>No lead stage found.</div>
            )}
          </div>

          <Pagination
            page={stagePage}
            totalPages={totalStagePages}
            setPage={setStagePage}
          />
        </div>

        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Lead Statuses</h2>
            {/* {isAdmin && (
              <button
                type="button"
                style={s.editBtn}
                onClick={() => {
                  setStatusesRaw(
                    JSON.stringify(config.statuses || {}, null, 2),
                  );
                  setShowStatusInput((prev) => !prev);
                }}
              >
                {showStatusInput ? (
                  <RemoveIcon style={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                )}
              </button>
            )} */}
          </div>

          {showStatusInput && (
            <div style={{ marginBottom: 12 }}>
              <label style={globalStyles.label}>Statuses JSON</label>
              <textarea
                style={{
                  ...globalStyles.textarea,
                  ...(getFieldError("statuses") ? globalStyles.inputError : {}),
                }}
                value={statusesRaw}
                onChange={(e) => {
                  setStatusesRaw(e.target.value);
                  clearFieldError("statuses");
                }}
                placeholder={`{\n  "prospecting": ["pending", "in_progress"]\n}`}
              />
              {getFieldError("statuses") && (
                <div style={globalStyles.errorText}>
                  {getFieldError("statuses")}
                </div>
              )}
            </div>
          )}

          <SearchBox
            value={statusSearch}
            onChange={(e) => {
              setStatusSearch(e.target.value);
              setStatusPage(1);
            }}
            placeholder="Search Lead Status"
          />

          <div style={s.listWrap}>
            {pagedStatuses.length > 0 ? (
              pagedStatuses.map((item) => (
                <div key={`${item.stage}-${item.status}`} style={s.listItem}>
                  <span style={s.itemName}>
                    {item.status.replaceAll("_", " ")}
                  </span>
                  <span style={s.itemMeta}>
                    {item.stage.replaceAll("_", " ")}
                  </span>
                </div>
              ))
            ) : (
              <div style={s.empty}>No lead status found.</div>
            )}
          </div>

          <Pagination
            page={statusPage}
            totalPages={totalStatusPages}
            setPage={setStatusPage}
          />
        </div>
      </div>

      <div style={s.row}>
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Business Types</h2>
            {/* {isAdmin && (
              <button
                type="button"
                style={s.editBtn}
                onClick={() => setShowBizInput((prev) => !prev)}
              >
                {showBizInput ? (
                  <RemoveIcon style={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                )}
              </button>
            )} */}
          </div>

          {showBizInput && (
            <div style={{ marginTop: 4, marginBottom: 4 }}>
              <label style={globalStyles.label}>Add business types</label>
              <TagArrayInput
                value={businessTypes}
                onChange={handleBusinessTypesChange}
                placeholder="e.g. digital cable"
                hasError={
                  !!getFieldError("businessType") ||
                  !!getFieldError("businessTypes")
                }
                normalize={(raw) =>
                  raw.trim().toLowerCase().replace(/\s+/g, " ")
                }
              />
              {(getFieldError("businessType") ||
                getFieldError("businessTypes")) && (
                <div style={globalStyles.errorText}>
                  {getFieldError("businessType") ||
                    getFieldError("businessTypes")}
                </div>
              )}
            </div>
          )}

          <SearchBox
            value={businessSearch}
            onChange={(e) => {
              setBusinessSearch(e.target.value);
              setBusinessPage(1);
            }}
            placeholder="Search Business Type"
          />

          <div style={s.listWrap}>
            {pagedBusinessTypes.length > 0 ? (
              pagedBusinessTypes.map((type) => (
                <div key={type} style={s.listItem}>
                  <span style={s.itemName}>{type}</span>
                  {showBizInput ? (
                    <button
                      type="button"
                      style={s.inlineRemoveBtn}
                      onClick={() =>
                        handleBusinessTypesChange(
                          businessTypes.filter((item) => item !== type),
                        )
                      }
                    >
                      <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                  ) : (
                    <span style={s.itemMeta}>Business</span>
                  )}
                </div>
              ))
            ) : (
              <div style={s.empty}>No business type found.</div>
            )}
          </div>

          <Pagination
            page={businessPage}
            totalPages={totalBusinessPages}
            setPage={setBusinessPage}
          />
        </div>

        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Languages</h2>
            {/* {isAdmin && (
              <button
                type="button"
                style={s.editBtn}
                onClick={() => setShowLangInput((prev) => !prev)}
              >
                {showLangInput ? (
                  <RemoveIcon style={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                )}
              </button>
            )} */}
          </div>

          {showLangInput && (
            <div style={{ marginBottom: 12 }}>
              <label style={globalStyles.label}>Add language</label>
              <div style={s.addInputRow}>
                <input
                  style={{ ...s.addInput, maxWidth: 90 }}
                  value={newLangKey}
                  onChange={(e) => setNewLangKey(e.target.value)}
                  placeholder="Key"
                  maxLength={8}
                />
                <input
                  style={s.addInput}
                  value={newLangName}
                  onChange={(e) => setNewLangName(e.target.value)}
                  placeholder="Display Name"
                  onKeyDown={(e) => e.key === "Enter" && handleAddLang()}
                />
                <button type="button" style={s.addBtn} onClick={handleAddLang}>
                  <AddIcon style={{ fontSize: 15 }} />
                  Add
                </button>
              </div>
            </div>
          )}

          <SearchBox
            value={languageSearch}
            onChange={(e) => {
              setLanguageSearch(e.target.value);
              setLanguagePage(1);
            }}
            placeholder="Search Language"
          />

          <div style={s.listWrap}>
            {pagedLanguages.length > 0 ? (
              pagedLanguages.map((lang) => (
                <div key={lang.key} style={s.listItem}>
                  <span style={{ ...s.itemName, flex: 1 }}>
                    {lang.displayName}
                  </span>

                  <span style={s.itemMeta}>{lang.key}</span>

                  {showLangInput && (
                    <button
                      type="button"
                      style={s.inlineRemoveBtn}
                      onClick={() => handleRemoveLang(lang.key)}
                    >
                      <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div style={s.empty}>No language found.</div>
            )}
          </div>

          <Pagination
            page={languagePage}
            totalPages={totalLanguagePages}
            setPage={setLanguagePage}
          />
        </div>
      </div>

      {/* <div style={s.row}>
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Incentives</h2>
            {isAdmin && (
              <button
                type="button"
                style={s.editBtn}
                onClick={() => setShowIncentiveInput((prev) => !prev)}
              >
                {showIncentiveInput ? (
                  <RemoveIcon style={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                )}
              </button>
            )}
          </div>

          <div style={s.listWrap}>
            {["per_lead", "per_demo", "per_closure"].map((key) => (
              <div key={key} style={s.incentiveValueRow}>
                <div style={s.incentiveName}>
                  {INCENTIVE_DOTS[key]}
                  {key.replaceAll("_", " ")}
                </div>
                {showIncentiveInput ? (
                  <input
                    type="number"
                    min="0"
                    style={{ ...s.input, width: 120, marginBottom: 0 }}
                    value={config.incentives?.[key] ?? 0}
                    onChange={(e) => handleIncentiveChange(key, e.target.value)}
                  />
                ) : (
                  <div style={s.incentiveAmount}>
                    ₹{Number(config.incentives?.[key] || 0)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.h2}>Notification Settings</h2>
          </div>

          {NOTIF_CHANNELS.map((key) => {
            const enabled = !!config.notificationSettings?.[key];
            return (
              <div key={key} style={s.toggleRow}>
                <div style={s.notifIcon}>
                  {NOTIF_ICONS[key]}
                  <div>
                    <div style={s.notifLabel}>{key}</div>
                    <div
                      style={{
                        ...s.notifStatus,
                        color: enabled ? "#10b981" : "#ef4444",
                      }}
                    >
                      {enabled ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  style={s.toggleBtn}
                  onClick={() => handleToggle(key)}
                >
                  {enabled ? (
                    <ToggleOnIcon style={{ fontSize: 38, color: "#10b981" }} />
                  ) : (
                    <ToggleOffIcon style={{ fontSize: 38, color: "#ef4444" }} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div> */}

      {/* <div style={s.saveRow}>
        <button
          type="button"
          style={s.btn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <span style={s.spinner} />
          ) : (
            <SaveOutlinedIcon style={{ fontSize: 16 }} />
          )}
          {saving ? "Saving..." : "Save Configuration"}
        </button>
      </div> */}
    </div>
  );
}
